/**
 * Iter-188 — Double-door leaf tagging (shared).
 *
 * On a double door the inspector records WHICH leaf each failed component
 * is on (`failed_component_photos[i].leaf`). The report renderers group the
 * faults under Leaf 1 / Leaf 2 / Both leaves headings, the Remedial Action
 * Schedule labels each line with its leaf, and the quote engine doubles the
 * quantity for "both". Single doors never carry the field.
 */
export type LeafChoice = 'leaf1' | 'leaf2' | 'both';

export const LEAF_ORDER: LeafChoice[] = ['leaf1', 'leaf2', 'both'];
export const LEAF_HEADING: Record<LeafChoice, string> = {
  leaf1: 'Leaf 1 — opening leaf',
  leaf2: 'Leaf 2 — fixed leaf',
  both: 'Both leaves',
};
export const LEAF_SHORT: Record<LeafChoice, string> = {
  leaf1: 'Leaf 1',
  leaf2: 'Leaf 2',
  both: 'Both leaves',
};

export const isLeafChoice = (v: unknown): v is LeafChoice => v === 'leaf1' || v === 'leaf2' || v === 'both';

export const isDoubleDoor = (door: any, inspection?: any): boolean =>
  door?.door_leaf_type === 'double' || inspection?.checklist_data?.door_leaf_type === 'double';

export interface LeafGroup<T> {
  leaf: LeafChoice | null;
  heading: string | null;
  items: T[];
}

/**
 * Split failed components into leaf groups (Leaf 1 → Leaf 2 → Both). When
 * the door is single, or none of the entries carry a leaf tag, everything is
 * returned as ONE unheaded group so existing layouts are untouched.
 */
export function groupByLeaf<T extends { leaf?: string | null }>(items: T[], isDouble: boolean): LeafGroup<T>[] {
  const tagged = isDouble && items.some((fc) => isLeafChoice(fc?.leaf));
  if (!tagged) return [{ leaf: null, heading: null, items }];
  return LEAF_ORDER
    .map((leaf) => ({
      leaf,
      heading: LEAF_HEADING[leaf],
      items: items.filter((fc) => (isLeafChoice(fc?.leaf) ? fc.leaf : 'both') === leaf),
    }))
    .filter((g) => g.items.length > 0);
}

/** " — Leaf 2" style suffix for schedule / list labels; empty when not applicable. */
export const leafSuffix = (leaf: unknown, isDouble: boolean): string =>
  isDouble && isLeafChoice(leaf) ? ` — ${LEAF_SHORT[leaf]}` : '';
